/**
 * AgentLayout — minimal chrome for the agent (trainee) self-learning portal.
 * Agents have no access to the trainer sidebar; this is a focused top-bar shell.
 */
import { Outlet, Link, useLocation } from 'react-router-dom';
import { GraduationCap, LogOut, BookOpen, ClipboardCheck, History } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import NotificationBell from '../pages/agent/NotificationBell';

export default function AgentLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  // No role gate: staff whose email is also enrolled as a trainee use this
  // portal too (backend AgentContext resolves access; pages show a clear
  // error card for staff with no trainee record).

  const name = user?.full_name || user?.first_name || user?.name || 'Agent';
  const initials = name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  const navCls = (active) =>
    `inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition ${
      active ? 'text-gold-700 shadow-[inset_0_-1px_0_0_#b68235]' : 'text-muted-foreground hover:text-foreground'}`;

  return (
    <div className="min-h-screen bg-background" data-testid="agent-layout">
      <header className="sticky top-0 z-40 bg-background border-b">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/my-learning" className="flex items-center gap-2" data-testid="agent-home-link">
              <GraduationCap className="w-6 h-6 text-[var(--lumyn-content,#F59E0B)]" />
              <span className="font-display font-bold text-lg">Lumyn</span>
            </Link>
            <nav className="flex items-center gap-1">
              <Link to="/my-learning" className={navCls(location.pathname.startsWith('/my-learning'))}
                    data-testid="nav-my-learning">
                <BookOpen className="w-4 h-4" /> My Learning
              </Link>
              <Link to="/my-assessments" className={navCls(location.pathname.startsWith('/my-assessments'))}
                    data-testid="nav-my-assessments">
                <ClipboardCheck className="w-4 h-4" /> My Assessments
              </Link>
              <Link to="/my-history" className={navCls(location.pathname.startsWith('/my-history'))}
                    data-testid="nav-my-history">
                <History className="w-4 h-4" /> History
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold-100 text-gold-800 flex items-center justify-center text-xs font-semibold">
                {initials}
              </div>
              <span className="text-sm font-medium hidden sm:block" data-testid="agent-name">{name}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button" aria-label="Log out">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
